import { useCallback, useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import {
	replaceSelectedNode,
	type UseExtensionParametersAndActionsProps,
} from '@atlassian/company-hub-common';

import { useHubElementPlaceholderExtensionsActions } from './useHubElementPlaceholderExtensions';

export type HubElementPlaceholderActions = {
	replaceHubElementPlaceholderNode: (nodeAdf: ADFEntity) => void;
	updatePreviewDetails: (extensionLocalId: string, previewComponent: React.ReactNode) => void;
	resetPreviewDetails: () => void;
};

export type UseHubElementsParametersAndActionsReturnType = {
	hubElementPlaceholderActions: HubElementPlaceholderActions;
};

export const useHubElementsPlaceholderActions = ({
	editorActions,
}: Pick<
	UseExtensionParametersAndActionsProps,
	'editorActions'
>): UseHubElementsParametersAndActionsReturnType => {
	const { setPreviewContentForHubElementPlaceholder } = useHubElementPlaceholderExtensionsActions();

	const replaceHubElementPlaceholderNode = useCallback(
		(nodeAdf: ADFEntity) => {
			replaceSelectedNode(editorActions, nodeAdf);
		},
		[editorActions],
	);

	const updatePreviewDetails = useCallback(
		(extensionLocalId: string, previewComponent: React.ReactNode) => {
			setPreviewContentForHubElementPlaceholder(extensionLocalId, previewComponent);
		},
		[setPreviewContentForHubElementPlaceholder],
	);

	const resetPreviewDetails = useCallback(() => {
		setPreviewContentForHubElementPlaceholder(null, null);
	}, [setPreviewContentForHubElementPlaceholder]);

	const hubElementPlaceholderActions = useMemo(() => {
		return {
			replaceHubElementPlaceholderNode,
			updatePreviewDetails,
			resetPreviewDetails,
		};
	}, [replaceHubElementPlaceholderNode, updatePreviewDetails, resetPreviewDetails]);

	return { hubElementPlaceholderActions };
};
